import { MenuItem, TextField } from '@material-ui/core';
import React, { useMemo } from 'react';
import type { FormField } from '../interfaces';
import { useField, useFormikContext } from 'formik';
import {
    evaluateExpression,
    findCamundaPropertyValue,
    getLocalizedLabel,
} from '../utils';
import { useGetDynamicFormDataQuery } from '../services/rtkApi';
import { Loader } from '@klp/design/Loader';
import type { FormValuesType } from './FormContainer';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const ConditionalAssignSelector = ({
    isRequired,
    isReadOnly,
    field,
}: {
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
}) => {
    const classes = getFormFieldStyles();
    const [formikField, meta] = useField(field?.id as string);
    const { values } = useFormikContext<FormValuesType>();

    const apiProperty = findCamundaPropertyValue('api', field?.properties);
    const visibleExpression = findCamundaPropertyValue(
        'visibleExpression',
        field?.properties,
    );

    const { data, isLoading: getDynamicFormDataIsLoading } =
        useGetDynamicFormDataQuery(
            { endpoint: apiProperty || '' },
            {
                skip: !apiProperty,
            },
        );

    const evaluatedExpression = useMemo(
        () =>
            visibleExpression
                ? evaluateExpression<boolean>(visibleExpression, values)
                : undefined,
        [visibleExpression, values],
    );

    return (
        <Loader isLoading={getDynamicFormDataIsLoading}>
            {evaluatedExpression ? (
                <TextField
                    select={true}
                    fullWidth={true}
                    className={classNames({
                        [classes.editableField]: !isReadOnly,
                        [classes.readOnlyField]: isReadOnly,
                    })}
                    required={isRequired}
                    variant="outlined"
                    {...formikField}
                    id={field?.id}
                    defaultValue={field?.value ?? field?.defaultValue}
                    disabled={!!isReadOnly}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    label={getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field?.label,
                        field?.id,
                    )}
                    error={meta.touched && Boolean(meta.error)}
                >
                    {/* // Need to formate the data to match the expected format while doing so in a generic way */}
                    {data?.map((v) => (
                        <MenuItem key={v?.id} value={v?.id}>
                            {v?.value}
                        </MenuItem>
                    )) ?? <MenuItem key={undefined} value={undefined} />}
                </TextField>
            ) : null}
        </Loader>
    );
};
